import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Container, Spinner, Alert, Button, Form, Row, Col, Card } from 'react-bootstrap';
import { useLazyQuery, useMutation } from '@apollo/client';
import { GET_INVENTORY_BY_ID } from 'api/graphql/queries/inventory/getInventoryById';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBoxOpen, faShippingFast, faFile, faCheck, faBox, faTimesCircle, faEdit, faCalendarAlt, faTrash, faPlus, faSave, faTimes } from '@fortawesome/free-solid-svg-icons';
import { SELLER_PRODUCTS_QUERY } from 'api/graphql/queries/product/GetInvProducts';
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import Select from "react-select";
import { UPDATE_INVENTORY_REQUEST } from 'api/graphql/mutations/inventory/updateInventoryRequest';

// Components
import InventoryHeader from 'components/modules/e-commerce/inventory/InventoryHeader';
import StatusCard from 'components/modules/e-commerce/inventory/StatusCard';
import DateInfoCard from 'components/modules/e-commerce/inventory/DateInfoCard';
import ProductsListCard from 'components/modules/e-commerce/inventory/ProductsListCard';
import ActionButtons from 'components/modules/e-commerce/inventory/ActionButtons';

interface InventoryItem {
  ID: string;
  Qty: string;
  Aqty?: string;
  product_id?: string;
  quantity_done?: number;
  productName?: string;
  quantity?: number;
  imageUrl?: string;
  currentStock?: number;
  seller_reference?: string;
}

interface InventoryDetails {
  inv_id: string;
  status: string;
  "date prévue"?: string;
  date_demande?: string;
  date_prevue?: string;
  date_done?: string;
  move_type?: string;
  stock_type?: string;
  inventory_list: InventoryItem[];
}

interface ProductOption {
  value: string;
  label: string;
  image: string;
  seller_reference: string;
  stockQty: number;
}

interface EditInventoryLine {
  ID: string;
  Qty: string;
  product_id: string;
  quantity: number;
  productName?: string;
  isNew?: boolean;
  currentStock?: number;
  imageUrl?: string;
  seller_reference?: string;
}

const InventoryDetailsPage: React.FC = () => {
  const { t } = useTranslation();
  // Get the inventory ID directly as it's in the route path
  const params = useParams<{ inventoryId: string }>();
  const inventoryId = params.inventoryId;
  console.log('Inventory ID from URL params:', inventoryId);
  
  const navigate = useNavigate();

  // States
  const [inventory, setInventory] = useState<InventoryDetails | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [productDetails, setProductDetails] = useState<Record<string, any>>({});
  const [productsLoading, setProductsLoading] = useState(false);
  
  // Edit states
  const [isEditing, setIsEditing] = useState(false);
  const [editDate, setEditDate] = useState<Date | undefined>(undefined);
  const [editItems, setEditItems] = useState<EditInventoryLine[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [productOptions, setProductOptions] = useState<ProductOption[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [isMenuOpen, setIsMenuOpen] = useState<boolean>(false);
  const [hasFormChanges, setHasFormChanges] = useState<boolean>(false);
  const [originalDate, setOriginalDate] = useState<Date | undefined>(undefined);
  const [originalItems, setOriginalItems] = useState<EditInventoryLine[]>([]);
  
  // GraphQL queries and mutations
  const [fetchProducts, { loading: queryLoading }] = useLazyQuery(SELLER_PRODUCTS_QUERY);
  const [getInventoryById, { loading: inventoryLoading, error: inventoryError, data: inventoryData }] = useLazyQuery(GET_INVENTORY_BY_ID);
  const [updateInventoryRequestMutation, { loading: updateLoading }] = useMutation(UPDATE_INVENTORY_REQUEST);
  
  // Fetch products by IDs
  const fetchProductDetails = useCallback(async (productIds: string[]) => {
    if (!productIds || productIds.length === 0) return;
    
    setProductsLoading(true);
    try {
      // Convert string IDs to integers and filter out invalid ones
      const numericIds = productIds.map(id => parseInt(id, 10)).filter(id => !isNaN(id));
      
      if (numericIds.length === 0) {
        console.warn("No valid product IDs to fetch");
        setProductsLoading(false);
        return;
      }
      
      const { data } = await fetchProducts({
        variables: {
          page: 1,
          pageSize: 100, // Set a large enough limit to get all products
          filter: {
            ids: numericIds
          }
        }
      });
      
      // Create a map of productId -> productData for easy lookup
      const productsMap: Record<string, any> = {};
      
      if (data?.sellerProducts?.products && Array.isArray(data.sellerProducts.products)) {
        data.sellerProducts.products.forEach((product: any) => {
          if (product?.entityid) {
            productsMap[product.entityid.toString()] = {
              id: product.entityid.toString(),
              name: product.name || `Product ${product.entityid}`,
              seller_reference: product.seller_reference || '',
              imageUrl: product.image1?.url || 'https://via.placeholder.com/50',
              stockQty: product.stock_data?.qty || 0,
            };
          }
        });
      }
      
      console.log('Fetched product details:', productsMap);
      setProductDetails(productsMap);
    } catch (error) {
      console.error("Error fetching product details:", error);
      toast.error("Failed to load product details");
    } finally {
      setProductsLoading(false);
    }
  }, [fetchProducts]);

  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const options: Intl.DateTimeFormatOptions = { 
        year: 'numeric', 
        month: 'short', 
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      };
      return date.toLocaleDateString('en-US', options);
    } catch (error) {
      return dateString;
    }
  };

  // Status info configuration with consistent icon sizes
  const getStatusInfo = (status: string) => {
    const statusMap: Record<string, { color: string, icon: JSX.Element, label: string }> = {
      'draft': {
        color: 'secondary',
        icon: <FontAwesomeIcon icon={faFile} style={{ fontSize: 22 }} />,
        label: t("inventoryHistory.status.draft")
      },
      'assigned': {
        color: 'primary',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="16.5" y1="9.4" x2="7.5" y2="4.21"></line><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>,
        label: t("inventoryHistory.status.assigned")
      },
      'done': {
        color: 'success',
        icon: <FontAwesomeIcon icon={faCheck} style={{ fontSize: 22 }} />,
        label: t("inventoryHistory.status.done")
      },
      'cancel': {
        color: 'danger',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>,
        label: t("inventoryHistory.status.cancelled")
      }
    };
    
    return statusMap[status.toLowerCase()] || {
      color: 'secondary',
      icon: <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: 22 }} />,
      label: status
    };
  };
  
  // Move type info configuration with consistent icon sizes
  const getMoveTypeInfo = (moveType: string) => {
    const moveTypeMap: Record<string, { color: string, icon: JSX.Element, label: string }> = {
      'in': {
        color: 'success',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><polyline points="19 12 12 19 5 12"></polyline></svg>,
        label: t("inventoryHistory.moveType.in")
      },
      'out': {
        color: 'danger',
        icon: <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>,
        label: t("inventoryHistory.moveType.out")
      }
    };
    
    return moveTypeMap[moveType.toLowerCase()] || {
      color: 'info',
      icon: <FontAwesomeIcon icon={faBoxOpen} style={{ fontSize: 22 }} />,
      label: moveType || 'Unknown'
    };
  };

  // Fetch inventory data using GraphQL
  const fetchInventory = useCallback(async () => {
    console.log('fetchInventory called with inventoryId:', inventoryId);
    
    if (!inventoryId) {
      console.error('No inventory ID provided');
      setError('No inventory ID provided');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      console.log('Making GraphQL query to getInventoryById with ID:', inventoryId);
      await getInventoryById({
        variables: {
          inventory_id: inventoryId
        },
        fetchPolicy: 'network-only' // Force a network request instead of using cache
      });
      
    } catch (error) {
      console.error('Error fetching inventory:', error);
      setError('An error occurred while loading inventory data');
      toast.error('Error loading inventory details');
    }
  }, [inventoryId, getInventoryById]);
  
  // Process GraphQL inventory data when it changes
  useEffect(() => {
    console.log('Processing inventory data effect', { inventoryLoading, hasData: !!inventoryData });
    
    if (!inventoryLoading && inventoryData) {
      try {
        const result = inventoryData.getInventoryById;
        console.log('GraphQL response:', result);
        
        if (result.status === 'success' && result.inventory_requests && result.inventory_requests.length > 0) {
          // Get the first inventory request from the response
          const data = result.inventory_requests[0];
          
          // Map Move Type directly from API if available
          if (data["Move Type"] && !data.move_type) {
            data.move_type = data["Move Type"];
          }
          
          console.log('GraphQL data to be used:', data);
          setInventory(data);
          
          // Extract product IDs to fetch product details
          if (data.inventory_list && Array.isArray(data.inventory_list)) {
            const productIds = data.inventory_list
              .map((item: InventoryItem) => item.product_id || item.ID)
              .filter((id: string | undefined): id is string => !!id);
            
            if (productIds.length > 0) {
              console.log('Fetching details for product IDs:', productIds);
              fetchProductDetails(productIds);
            }
          }
          
          setLoading(false);
        } else {
          console.error('Invalid GraphQL response:', result);
          setError('Failed to fetch inventory data');
          setLoading(false);
        }
      } catch (error) {
        console.error('Error processing GraphQL response:', error);
        setError('Error processing inventory data');
        setLoading(false);
      }
    } else if (!inventoryLoading && inventoryError) {
      console.error('GraphQL error:', inventoryError);
      setError(inventoryError.message || 'Error fetching inventory data');
      setLoading(false);
    }
  }, [inventoryData, inventoryLoading, inventoryError, fetchProductDetails]);
  
  // Get query params
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const shouldStartEditing = searchParams.get('edit') === 'true';
  
  // First effect - just for fetching inventory
  useEffect(() => {
    fetchInventory();
  }, [fetchInventory]);
  
  // Second effect - separately check for edit mode activation
  useEffect(() => {
    // Only run when inventory data is loaded, not loading, and product details are loaded
    if (!loading && 
        !productsLoading && 
        inventory && 
        shouldStartEditing && 
        inventory.status.toLowerCase() === 'draft' && 
        Object.keys(productDetails).length > 0) {
      startEditing();
    }
  }, [inventory, loading, shouldStartEditing, productsLoading, productDetails]);
  
  // Format date for API
  const formatDateForApi = (date: Date | undefined): string => {
    if (!date) return "";
    
    const pad = (num: number) => num.toString().padStart(2, "0");
    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:00`;
  };
  
  // Start editing mode
  const startEditing = () => {
    if (!inventory || inventory.status.toLowerCase() !== 'draft') {
      return;
    }
    
    // Set the date
    const initialDate = new Date(inventory.date_prevue || '');
    setEditDate(initialDate);
    setOriginalDate(initialDate);
    
    // Load inventory items
    const inventoryList = inventory.inventory_list || [];
    
    // Map inventory items to edit format
    const items = inventoryList.map((item) => {
      const productId = item.product_id || item.ID || "";
      const productDetail = productId ? productDetails[productId] : undefined;
      const quantity_done = typeof item.quantity_done === 'number' ? item.quantity_done : 0;
      const qtyFromString = parseInt(item.Qty || "0") || 0;
      const itemQty = quantity_done || qtyFromString;
      
      return {
        ID: item.ID || item.product_id || "",
        Qty: item.Qty || (item.quantity_done?.toString() || "0"),
        product_id: productId,
        quantity: itemQty,
        productName: productDetail?.name || "Unknown Product",
        currentStock: productDetail?.stockQty || 0,
        imageUrl: productDetail?.imageUrl || "https://via.placeholder.com/50",
        seller_reference: productDetail?.seller_reference || "No REF",
        isNew: false
      };
    });
    
    setEditItems(items);
    setOriginalItems([...items]); // Store original items for change detection
    setIsEditing(true);
    setHasFormChanges(false);
    
    // Load product options
    loadProducts("");
  };
  
  // Load products for dropdown
  const loadProducts = async (inputValue: string, page = 1) => {
    try {
      setSearchTerm(inputValue);
      
      if (!inventory) {
        return;
      }
      
      // Determine the stock type and move type from the inventory
      const moveType = inventory.move_type?.toLowerCase() || '';
      let stockType: string;
      
      // Handle stock type which can come as a string or a boolean
      if (typeof inventory.stock_type === 'string') {
        stockType = inventory.stock_type as string;
      } else {
        stockType = inventory.stock_type ? 'FBW' : 'drop';
      }
      
      // Create filter object with all needed properties
      const filter: any = {
        type_id: "simple", // Always use simple products
        enabled_with_parents: true, // Only enabled products with enabled parents
        // Set mode_expedition filter based on stock type
        ...(stockType === 'FBW' 
          ? { mode_expedition: 16500 } // FBW products
          : { mode_expedition_neq: 16500 }), // drop (Easy Shipping) products
        
        // For "out" movement, add filter for products with stock > 0
        ...(moveType === 'out' ? { qty_gt: 0 } : {}),
        
        // Add search term filter if provided
        ...(inputValue && inputValue.length > 0 ? { name: inputValue } : {})
      };
      
      const { data } = await fetchProducts({
        variables: {
          page: page,
          pageSize: 20,
          filter: filter,
        },
      });
      
      if (data?.sellerProducts?.products) {
        const newOptions = data.sellerProducts.products.map((product: any) => ({
          value: product?.entityid?.toString() || "N/A",
          label: product?.name || "Unnamed Product",
          image: product?.image1?.url || "https://via.placeholder.com/50",
          stockQty: product?.stock_data?.qty || 0,
          seller_reference: product?.seller_reference || "No REF",
        }));
        
        // If page is 1, replace options; otherwise append
        if (page === 1) {
          setProductOptions(newOptions);
        } else {
          // Append new options, avoiding duplicates
          setProductOptions(prevOptions => {
            const existingIds = new Set(prevOptions.map((p: ProductOption) => p.value));
            const uniqueNewOptions = newOptions.filter((p: ProductOption) => !existingIds.has(p.value));
            return [...prevOptions, ...uniqueNewOptions];
          });
        }
        
        // Update current page
        setCurrentPage(page);
      }
    } catch (error) {
      console.error("Error loading products:", error);
    }
  };
  
  // Handle quantity change
  const handleQuantityChange = (index: number, quantity: number) => {
    const updatedItems = [...editItems];
    
    // For "out" move type, ensure quantity doesn't exceed current stock
    if (inventory?.move_type?.toLowerCase() === "out" && 
        quantity > (updatedItems[index].currentStock || 0)) {
      toast.warning(`Cannot exceed current stock of ${updatedItems[index].currentStock}`);
      quantity = updatedItems[index].currentStock || 0;
    }
    
    // Create a new object to ensure state change detection works properly
    const updatedItem = {
      ...updatedItems[index],
      quantity: quantity,
      Qty: quantity.toString()
    };
    
    // Replace the item at the index with the updated item
    updatedItems[index] = updatedItem;
    
    // Update state with the new array to ensure proper re-rendering
    setEditItems([...updatedItems]);
    setHasFormChanges(true);
  };
  
  // Add a new product row
  const handleAddProduct = () => {
    setEditItems([
      ...editItems,
      {
        ID: "", // Will be filled when a product is selected
        Qty: "0",
        product_id: "",
        quantity: 0,
        productName: "Select a product",
        isNew: true
      }
    ]);
    setHasFormChanges(true);
  };
  
  // Delete a product row
  const handleDeleteProduct = (index: number) => {
    // Prevent deletion if there's only one product
    if (editItems.length <= 1) {
      toast.warning("At least one product must be included");
      return;
    }
    
    const updatedItems = [...editItems];
    updatedItems.splice(index, 1);
    setEditItems(updatedItems);
    setHasFormChanges(true);
  };
  
  // Handle product selection
  const handleProductChange = async (selected: any, index: number) => {
    if (!selected) return;
    
    const productId = selected.value;
    
    // Check if this product is already selected in another row
    const isDuplicate = editItems.some((item, idx) => 
      idx !== index && item.product_id === productId && !item.isNew
    );
    
    if (isDuplicate) {
      toast.error("This product is already selected in another row.");
      return;
    }
    
    const updatedItems = [...editItems];
    
    // Create a new object with updated properties
    const updatedItem = {
      ...updatedItems[index],
      ID: productId,
      product_id: productId,
      productName: selected.label,
      currentStock: selected.stockQty || 0,
      imageUrl: selected.image,
      seller_reference: selected.seller_reference,
      isNew: false // No longer a new item once a product is selected
    };
    
    // Replace the item at the index
    updatedItems[index] = updatedItem;
    
    // Use a new array reference to ensure state change is detected
    setEditItems([...updatedItems]);
    setHasFormChanges(true);
  };
  
  // Handle menu scroll to bottom for pagination
  const handleMenuScrollToBottom = () => {
    if (isMenuOpen && !queryLoading) {
      const nextPage = currentPage + 1;
      loadProducts(searchTerm, nextPage);
    }
  };
  
  // Cancel editing
  const cancelEditing = () => {
    setIsEditing(false);
    setEditItems([]);
    setEditDate(undefined);
    setHasFormChanges(false);
    
    // Remove the edit=true parameter from URL
    if (shouldStartEditing) {
      navigate(`/inventory/details/${inventoryId}`, { replace: true });
    }
  };
  
  // Submit updated inventory request
  const handleUpdateSubmit = async () => {
    if (!inventory || !editDate) {
      toast.error("Missing required information");
      return;
    }
    
    // Validate that all products have valid ID and quantity
    const invalidItems = editItems.filter(item => !item.ID || item.quantity <= 0);
    if (invalidItems.length > 0) {
      toast.error(t("inventory.errors.selectProduct"));
      return;
    }
    
    // For "out" moves, validate quantities don't exceed current stock
    if (inventory?.move_type?.toLowerCase() === "out") {
      const overStockItems = editItems.filter(item => 
        item.quantity > (item.currentStock || 0)
      );
      
      if (overStockItems.length > 0) {
        toast.error(t("inventory.errors.exceedsStock"));
        return;
      }
    }
    
    setIsSubmitting(true);
    
    try {
      const formattedDate = formatDateForApi(editDate);
      // Use the ID and Qty properties directly since they're already in the correct format
      const items = editItems.map(item => {
        return {
          ID: item.ID,
          Qty: item.quantity.toString(), // New quantity
          Aqty: item.currentStock?.toString() || "0", // Always use the product's current stock quantity
        };
      });
      
      // Use GraphQL mutation
      const { data: updateData } = await updateInventoryRequestMutation({
        variables: {
          request_id: inventory.inv_id,
          date: formattedDate,
          items: items
        }
      });
      
      if (updateData?.updateInventoryRequest?.status === "success") {
        toast.success(updateData.updateInventoryRequest.message || "Inventory request updated successfully");
        setIsEditing(false);
        fetchInventory(); // Refresh the inventory data
      } else {
        toast.error(updateData?.updateInventoryRequest?.message || "Failed to update inventory request");
      }
    } catch (error) {
      console.error("Error updating inventory request:", error);
      toast.error("Failed to update inventory request");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" variant="primary" />
        <p className="mt-3">{t("inventoryDetails.loading")}</p>
      </Container>
    );
  }

  // Error state
  if (error || !inventory) {
    return (
      <Container className="py-5">
        <Alert variant="danger">
          {error || t("inventoryDetails.notFound")}
        </Alert>
        <div className="text-center mt-4">
          <button 
            className="btn btn-primary"
            onClick={() => navigate('/apps/e-commerce/admin/inventory-history')}
          >
            {t("inventoryDetails.backToList")}
          </button>
        </div>
      </Container>
    );
  }

  // Check if can edit (only draft status can be edited)
  const canEdit = inventory.status.toLowerCase() === 'draft';

  return (
    <Container className="py-2 py-md-4" fluid="lg">
      <div className="bg-body">
        {/* Header with back button and title */}
        <InventoryHeader 
          inventoryId={inventory.inv_id} 
          status={inventory.status}
          stockType={
            inventory.stock_type === undefined 
              ? "FBW" // Default value when undefined
              : typeof inventory.stock_type === 'boolean'
                ? (inventory.stock_type ? "FBW" : "drop")
                : String(inventory.stock_type) // Convert to string safely
          }
        />
        
        <div className="mt-3">
          {/* Status and move type */}
          <StatusCard
            status={inventory.status}
            statusInfo={getStatusInfo(inventory.status)}
            moveType={inventory.move_type || 'in'}
            moveTypeInfo={getMoveTypeInfo(inventory.move_type || 'in')}
            stockType={
              inventory.stock_type === undefined 
                ? "FBW" // Default value when undefined
                : typeof inventory.stock_type === 'boolean'
                  ? (inventory.stock_type ? "FBW" : "drop")
                  : String(inventory.stock_type) // Convert to string safely
            }
          />
          
          {/* Date information */}
          {!isEditing ? (
            <DateInfoCard
              requestedDate={inventory.date_demande || ''}
              scheduledDate={inventory.date_prevue || ''}
              formatDate={formatDate}
              stockType={
                inventory.stock_type === undefined 
                  ? "FBW" // Default value when undefined
                  : typeof inventory.stock_type === 'boolean'
                    ? (inventory.stock_type ? "FBW" : "drop")
                    : String(inventory.stock_type) // Convert to string safely
              }
              creationDate={inventory.date_demande || ''} // Use date_demande as creation date for drop stock
            />
          ) : (
            <Card 
              className="mb-4"
              style={{
                borderRadius: "18px",
                background: "var(--bs-card-bg)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                overflow: "hidden",
                border: "1px solid var(--bs-border-color)"
              }}
            >
              <Card.Body className="py-3 py-md-4 px-3 px-md-4">
                <Form.Group>
                  <Form.Label className="fw-bold mb-3">{t("inventoryDetails.scheduledDate")}</Form.Label>
                  <div className="input-group">
                    <span className="input-group-text bg-light border-end-0">
                      <FontAwesomeIcon icon={faCalendarAlt} className="text-secondary" />
                    </span> 
                    <Flatpickr
                      className="form-control border-start-0 ps-1"
                      value={editDate}
                      onChange={([date]) => {
                        setEditDate(date);
                        setHasFormChanges(true);
                      }}
                      options={{ 
                        enableTime: true, 
                        time_24hr: true,
                        dateFormat: "d/m/Y H:i",
                        allowInput: true
                      }}
                    />
                  </div>
                </Form.Group>
              </Card.Body>
            </Card>
          )}
          
          {/* Product list */}
          {!isEditing ? (
            <ProductsListCard
              items={inventory.inventory_list.map((item: InventoryItem) => {
                const productId = item.product_id || item.ID;
                const productDetail = productId ? productDetails[productId] : undefined;
                
                return {
                  ...item,
                  productName: productDetail?.name || `Product ${productId}`,
                  imageUrl: productDetail?.imageUrl || 'https://via.placeholder.com/50',
                  currentStock: productDetail?.stockQty || 0,
                  seller_reference: productDetail?.seller_reference || 'No REF'
                };
              })}
              title={t("inventoryDetails.products")}
              stockType={
                inventory.stock_type === undefined 
                  ? "FBW" // Default value when undefined
                  : typeof inventory.stock_type === 'boolean'
                    ? (inventory.stock_type ? "FBW" : "drop")
                    : String(inventory.stock_type) // Convert to string safely
              }
              moveType={inventory.move_type?.toLowerCase() || 'in'}
              status={inventory.status}
            />
          ) : (
            <Card
              className="mb-4"
              style={{
                borderRadius: "18px",
                background: "var(--bs-card-bg)",
                boxShadow: "0 6px 18px rgba(0,0,0,0.08)",
                transition: "all 0.3s ease",
                overflow: "hidden",
                border: "1px solid var(--bs-border-color)"
              }}
            >
              <Card.Header
                className="border-0 py-3 px-3 px-md-4 d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center"
                style={{
                  borderBottom: "1px solid rgba(0,0,0,0.06)"
                }}
              >
                <h5 className="mb-2 mb-md-0 fw-semibold" style={{ fontSize: "1.2rem" }}>{t("inventoryDetails.products")}</h5>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={handleAddProduct}
                  className="rounded-2 w-100 w-md-auto"
                  style={{ minWidth: "auto" }}
                >
                  <FontAwesomeIcon icon={faPlus} className="me-1" /> {t("inventory.addProductButton")}
                </Button>
              </Card.Header>
              <Card.Body className="p-0">
                {/* Mobile card layout for small screens */}
                <div className="d-block d-lg-none">
                  {editItems.map((item, index) => (
                    <div key={index} className="border-bottom p-3">
                      <div className="d-flex justify-content-between align-items-start mb-3">
                        <h6 className="mb-0 text-primary">Product {index + 1}</h6>
                        <Button
                          variant={editItems.length <= 1 ? "outline-secondary" : "outline-danger"}
                          size="sm"
                          onClick={() => handleDeleteProduct(index)}
                          disabled={editItems.length <= 1}
                        >
                          <FontAwesomeIcon icon={faTrash} />
                        </Button>
                      </div>
                      
                      {/* Product Selection */}
                      <div className="mb-3">
                        <label className="form-label small fw-semibold text-muted mb-2">{t("inventory.product")}</label>
                        {item.isNew ? (
                          <Select
                            options={productOptions.filter(option => 
                              !editItems.some(
                                item => item.product_id === option.value && !item.isNew
                              )
                            )}
                            placeholder={t("inventory.searchProduct")}
                            onChange={(selected) => handleProductChange(selected, index)}
                            isLoading={queryLoading}
                            onInputChange={(value) => {
                              if (value !== searchTerm) {
                                loadProducts(value, 1);
                              }
                            }}
                            onMenuOpen={() => {
                              setIsMenuOpen(true);
                              if (productOptions.length === 0) {
                                loadProducts("", 1);
                              }
                            }}
                            onMenuClose={() => setIsMenuOpen(false)}
                            onMenuScrollToBottom={handleMenuScrollToBottom}
                            formatOptionLabel={(option: ProductOption) => (
                              <div style={{ display: "flex", alignItems: "center" }}>
                                <div 
                                  style={{ 
                                    width: 30, 
                                    height: 30, 
                                    marginRight: 8, 
                                    borderRadius: "4px", 
                                    overflow: "hidden",
                                    border: "1px solid #dee2e6"
                                  }}
                                >
                                  <img
                                    src={option.image}
                                    alt={option.label}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                </div>
                                <div>
                                  <div style={{ fontWeight: 500, fontSize: "0.9rem" }}>{option.label}</div>
                                  <div style={{ fontSize: "0.75rem", color: "var(--bs-body-color-rgb)" }}>{t("inventory.ref")}: {option.seller_reference}</div>
                                </div>
                              </div>
                            )}
                            filterOption={() => true}
                            menuPortalTarget={document.body}
                            styles={{
                              menuPortal: (base) => ({
                                ...base,
                                zIndex: 9999
                              }),
                              control: (base) => ({
                                ...base,
                                minHeight: '38px'
                              })
                            }}
                          />
                        ) : (
                          <div className="d-flex align-items-center p-2 bg-light rounded">
                            <div 
                              style={{ 
                                width: 30, 
                                height: 30, 
                                marginRight: 8, 
                                borderRadius: "4px", 
                                overflow: "hidden",
                                border: "1px solid #dee2e6"
                              }}
                            >
                              <img
                                src={item.imageUrl || "https://via.placeholder.com/50"}
                                alt={item.productName}
                                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                              />
                            </div>
                            <div className="flex-grow-1">
                              <div className="fw-semibold small">{item.productName || "Unknown Product"}</div>
                              <div className="small text-muted">{t("inventory.ref")}: {item.seller_reference || t("inventory.noReference")}</div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      {/* Stock and Quantity Row */}
                      <Row className="g-2">
                        <Col xs={6}>
                          <label className="form-label small fw-semibold text-muted mb-1">{t("inventory.currentStock")}</label>
                          <div className="bg-light rounded p-2 text-center">
                            <span className="fw-semibold">{item.currentStock || 0}</span>
                          </div>
                        </Col>
                        <Col xs={6}>
                          <label className="form-label small fw-semibold text-muted mb-1">
                            {inventory?.move_type?.toLowerCase() === "in" 
                              ? t("inventory.stockAddition") 
                              : inventory?.move_type?.toLowerCase() === "out" 
                              ? t("inventory.stockReduction") 
                              : t("inventory.movement")}
                          </label>
                          <Form.Control 
                            type="number" 
                            min="0"
                            max={inventory?.move_type?.toLowerCase() === "out" ? item.currentStock || 0 : undefined}
                            value={item.quantity} 
                            onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                            className={`text-center ${
                              inventory?.move_type?.toLowerCase() === "out" && 
                              item.quantity > (item.currentStock || 0) ? "border-danger" : ""
                            }`}
                          />
                          {/* Warning for exceeding stock */}
                          {inventory?.move_type?.toLowerCase() === "out" && 
                          item.quantity > (item.currentStock || 0) && (
                            <div className="text-danger small mt-1">
                              {t("inventory.errors.exceedsStock")}
                            </div>
                          )}
                        </Col>
                      </Row>
                    </div>
                  ))}
                </div>
                
                {/* Desktop table layout for large screens */}
                <div className="table-responsive d-none d-lg-block">
                  <table className="table table-hover mb-0">
                    <thead className="bg-light">
                      <tr>
                        <th className="border-0 ps-4 py-3" style={{ width: "40%" }}>{t("inventory.product")}</th>
                        <th className="border-0 py-3 text-center" style={{ width: "20%" }}>{t("inventory.currentStock")}</th>
                        <th className="border-0 py-3 text-center" style={{ width: "25%" }}>
                          {inventory?.move_type?.toLowerCase() === "in" 
                            ? t("inventory.stockAddition") 
                            : inventory?.move_type?.toLowerCase() === "out" 
                            ? t("inventory.stockReduction") 
                            : t("inventory.movement")}
                        </th>
                        <th className="border-0 py-3 text-center" style={{ width: "15%" }}>{t("inventory.actions")}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {editItems.map((item, index) => (
                        <tr key={index}>
                          <td className="py-3 ps-4 align-middle">
                            {item.isNew ? (
                              <Select
                                options={productOptions.filter(option => 
                                  !editItems.some(
                                    item => item.product_id === option.value && !item.isNew
                                  )
                                )}
                                placeholder={t("inventory.searchProduct")}
                                onChange={(selected) => handleProductChange(selected, index)}
                                isLoading={queryLoading}
                                onInputChange={(value) => {
                                  if (value !== searchTerm) {
                                    loadProducts(value, 1);
                                  }
                                }}
                                onMenuOpen={() => {
                                  setIsMenuOpen(true);
                                  if (productOptions.length === 0) {
                                    loadProducts("", 1);
                                  }
                                }}
                                onMenuClose={() => setIsMenuOpen(false)}
                                onMenuScrollToBottom={handleMenuScrollToBottom}
                                formatOptionLabel={(option: ProductOption) => (
                                  <div style={{ display: "flex", alignItems: "center" }}>
                                    <div 
                                      style={{ 
                                        width: 40, 
                                        height: 40, 
                                        marginRight: 10, 
                                        borderRadius: "4px", 
                                        overflow: "hidden",
                                        border: "1px solid #dee2e6"
                                      }}
                                    >
                                      <img
                                        src={option.image}
                                        alt={option.label}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                      />
                                    </div>
                                    <div>
                                      <div style={{ fontWeight: 500 }}>{option.label}</div>
                                      <div style={{ fontSize: "0.8rem", color: "var(--bs-body-color-rgb)" }}>{t("inventory.ref")}: {option.seller_reference}</div>
                                    </div>
                                  </div>
                                )}
                                filterOption={() => true} // Skip built-in filtering, we'll handle it with API calls
                                menuPortalTarget={document.body}
                                styles={{
                                  menuPortal: (base) => ({
                                    ...base,
                                    zIndex: 9999
                                  })
                                }}
                              />
                            ) : (
                              <div className="d-flex align-items-center">
                                <div 
                                  style={{ 
                                    width: 40, 
                                    height: 40, 
                                    marginRight: 10, 
                                    borderRadius: "4px", 
                                    overflow: "hidden",
                                    border: "1px solid #dee2e6"
                                  }}
                                >
                                  <img
                                    src={item.imageUrl || "https://via.placeholder.com/50"}
                                    alt={item.productName}
                                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                                  />
                                </div>
                                <div>
                                  <div className="fw-semibold">{item.productName || "Unknown Product"}</div>
                                  <div className="small text-muted">{t("inventory.ref")}: {item.seller_reference || t("inventory.noReference")}</div>
                                </div>
                              </div>
                            )}
                          </td>
                          <td className="py-3 align-middle text-center">
                            <div className="d-inline-block px-3 py-2 rounded-1" style={{ backgroundColor: "var(--bs-tertiary-bg)", fontWeight: 500 }}>
                              {item.currentStock || 0}
                            </div>
                          </td>
                          <td className="py-3 align-middle">
                            <div className="d-flex flex-column align-items-center">
                              <div className="input-group mb-1" style={{ maxWidth: "100px" }}>
                                <Form.Control 
                                  type="number" 
                                  min="0"
                                  max={inventory?.move_type?.toLowerCase() === "out" ? item.currentStock || 0 : undefined}
                                  value={item.quantity} 
                                  onChange={(e) => handleQuantityChange(index, parseInt(e.target.value) || 0)}
                                  className={`text-center ${
                                    inventory?.move_type?.toLowerCase() === "out" && 
                                    item.quantity > (item.currentStock || 0) ? "border-danger" : ""
                                  }`}
                                />
                              </div>
                              
                              {/* Warning for exceeding stock */}
                              {inventory?.move_type?.toLowerCase() === "out" && 
                              item.quantity > (item.currentStock || 0) && (
                                <div className="text-danger small mt-1">
                                  {t("inventory.errors.exceedsStock")}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="py-3 align-middle text-center">
                            <Button
                              variant={editItems.length <= 1 ? "outline-secondary" : "outline-danger"}
                              size="sm"
                              onClick={() => handleDeleteProduct(index)}
                              title={editItems.length <= 1 ? "Cannot delete the last product" : "Delete this product"}
                              disabled={editItems.length <= 1}
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
          )}
          
          {productsLoading && (
            <div className="text-center my-3">
              <Spinner animation="border" variant="primary" size="sm" className="me-2" />
              <span className="text-body-secondary">{t("inventoryDetails.loadingProducts")}</span>
            </div>
          )}
          
          {/* Action buttons or edit mode actions */}
          {!isEditing ? (
            <ActionButtons
              canEdit={canEdit}
              onEdit={() => startEditing()}
            />
          ) : (
            <div className="d-flex flex-column flex-sm-row justify-content-end gap-2 mb-4">
              <Button 
                variant="outline-secondary" 
                onClick={cancelEditing}
                className="rounded-2 px-4 order-2 order-sm-1"
                disabled={isSubmitting}
              >
                <FontAwesomeIcon icon={faTimes} className="me-2" />
                {t("inventoryDetails.cancel")}
              </Button>
              <Button 
                variant="primary" 
                onClick={handleUpdateSubmit}
                className="rounded-2 px-4 order-1 order-sm-2"
                disabled={isSubmitting || !hasFormChanges}
              >
                {isSubmitting ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                    {t("inventoryDetails.updating", "Updating...")}
                  </>
                ) : (
                  <>
                    <FontAwesomeIcon icon={faSave} className="me-2" />
                    {t("inventoryDetails.updateRequest", "Update Request")}
                  </>
                )}
              </Button>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
};

export default InventoryDetailsPage;
